import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import ModeCard from '../../components/docs/ModeCard';
import CodeBlock from '../../components/docs/CodeBlock';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'text-mode', label: 'Text Mode' },
  { id: 'vision-mode', label: 'Vision Mode' },
  { id: 'comparison', label: 'Comparison' },
];

const textDataExample = `{
  "instrument": "NAS",
  "timestamp": "2025-01-10T14:30:00Z",
  "current_price": 21545.50,
  "candles": [
    { "time": "14:15", "o": 21520, "h": 21558, "l": 21515, "c": 21545, "v": 12450 },
    { "time": "14:00", "o": 21498, "h": 21525, "l": 21490, "c": 21520, "v": 10230 },
    // ... 48 more candles
  ],
  "market_context": {
    "session": "US",
    "day_of_week": "Friday",
    "recent_trend": "bullish"
  }
}`;

const visionPromptExample = `[Attached: chart_15m.png - 1024x1024 candlestick chart]

Analyze this 15-minute chart and make a trading decision.

Chart contains:
- 50 candlesticks with current price at right edge
- Price axis with grid lines
- Time axis labels

Current position: NONE
Account balance: $102,450`;

const TextVsVision = () => {
  const seoConfig = SEO_CONFIG['/docs/text-vs-vision'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="overview">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/text-vs-vision"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/text-vs-vision" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Text vs Vision Mode
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        Models receive market data in two different formats. Understanding these modes
        helps explain performance differences between models.
      </p>

      {/* Section 1: Overview */}
      <section id="overview" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Overview
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          The benchmark tests models in two input modes: text (structured data) and vision
          (chart images). Each mode presents the same market information in different formats.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <ModeCard
            mode="TEXT"
            color="green"
            description="Models receive structured numerical data including OHLCV candles and market context as formatted JSON."
            models={['Claude Haiku 4.5', 'Gemini 3 Flash', 'DeepSeek V3.2', 'GLM-4.7']}
          />
          <ModeCard
            mode="VISION"
            color="purple"
            description="Models receive rendered candlestick chart images. They analyze visual price patterns to make trading decisions."
            models={['Claude Opus 4.5', 'Gemini 3 Pro', 'GPT-5.2', 'GPT-5 Mini', 'Grok 4', 'Grok 4.1', 'GLM-4.6V', 'Kimi K2.5']}
          />
        </div>

        <Callout type="info" title="Same Information, Different Format">
          Both modes contain equivalent information. Text mode provides exact numbers while
          vision mode shows the same data visually. This tests whether models perform better
          with numerical precision or visual pattern recognition.
        </Callout>
      </section>

      {/* Section 2: Text Mode */}
      <section id="text-mode" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Text Mode
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Text mode provides structured JSON data with precise numerical values. Models
          must parse and interpret this data to make trading decisions.
        </p>

        <h3 className="font-semibold text-zinc-200 mb-3">Data Structure</h3>
        <CodeBlock filename="market_data.json" code={textDataExample} />

        <div className="mt-6 space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Included Data</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-zinc-400 mb-2">Price Data</div>
                <ul className="space-y-1 text-sm text-zinc-500">
                  <li>• 50 historical OHLCV candles</li>
                  <li>• Current bid/ask price</li>
                  <li>• Volume per candle</li>
                </ul>
              </div>
              <div>
                <div className="text-sm text-zinc-400 mb-2">Market Context</div>
                <ul className="space-y-1 text-sm text-zinc-500">
                  <li>• Trading session info</li>
                  <li>• Day of week</li>
                  <li>• Recent trend summary</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Text Mode Advantages</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Precise values</span>
                  <p className="text-sm text-zinc-500">Exact numbers enable precise calculations</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Smaller payload</span>
                  <p className="text-sm text-zinc-500">JSON is smaller than image data, faster API calls</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Lower cost</span>
                  <p className="text-sm text-zinc-500">Text tokens cost less than image processing</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Text Mode Challenges</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">−</span>
                <div>
                  <span className="text-zinc-200">Pattern recognition</span>
                  <p className="text-sm text-zinc-500">Harder to identify visual chart patterns from numbers</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">−</span>
                <div>
                  <span className="text-zinc-200">Context interpretation</span>
                  <p className="text-sm text-zinc-500">Must mentally construct price movement from data points</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 3: Vision Mode */}
      <section id="vision-mode" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Vision Mode
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Vision mode provides rendered chart images. Models with multimodal capabilities
          analyze the visual representation directly.
        </p>

        <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5 mb-6">
          <h4 className="font-semibold text-zinc-200 mb-3">Chart Specifications</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-zinc-500 mb-1">Resolution</div>
              <div className="text-zinc-200 font-mono">1024 × 1024</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Format</div>
              <div className="text-zinc-200 font-mono">PNG</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Timeframe</div>
              <div className="text-zinc-200 font-mono">15 min</div>
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Candles</div>
              <div className="text-zinc-200 font-mono">50</div>
            </div>
          </div>
        </div>

        <h3 className="font-semibold text-zinc-200 mb-3">Vision Prompt Example</h3>
        <CodeBlock filename="vision_prompt.txt" code={visionPromptExample} />

        <div className="mt-6 space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Chart Elements</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-zinc-400 mb-2">Price Display</div>
                <ul className="space-y-1 text-sm text-zinc-500">
                  <li>• Candlestick price bars</li>
                  <li>• Price axis with grid</li>
                  <li>• Current price marker</li>
                </ul>
              </div>
              <div>
                <div className="text-sm text-zinc-400 mb-2">Time & Context</div>
                <ul className="space-y-1 text-sm text-zinc-500">
                  <li>• Time axis labels</li>
                  <li>• Multiple timeframe views</li>
                  <li>• Visual price patterns</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Vision Mode Advantages</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Pattern recognition</span>
                  <p className="text-sm text-zinc-500">Can identify visual patterns like head-and-shoulders, flags, etc.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Holistic view</span>
                  <p className="text-sm text-zinc-500">Sees entire price action context at once</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-500 mt-0.5">+</span>
                <div>
                  <span className="text-zinc-200">Natural analysis</span>
                  <p className="text-sm text-zinc-500">Mirrors how human traders analyze charts</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <h4 className="font-semibold text-zinc-200 mb-3">Vision Mode Challenges</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">−</span>
                <div>
                  <span className="text-zinc-200">Imprecise values</span>
                  <p className="text-sm text-zinc-500">Must estimate exact prices from visual position</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">−</span>
                <div>
                  <span className="text-zinc-200">Higher cost</span>
                  <p className="text-sm text-zinc-500">Image processing costs more than text tokens</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5">−</span>
                <div>
                  <span className="text-zinc-200">Slower processing</span>
                  <p className="text-sm text-zinc-500">Image analysis takes longer than text parsing</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 4: Comparison */}
      <section id="comparison" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">04</span>
          Comparison
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Side-by-side comparison of the two input modes to help understand their differences.
        </p>

        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Aspect</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Text Mode</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Vision Mode</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-400">Input format</td>
                <td className="py-3 px-4 text-zinc-200">JSON data</td>
                <td className="py-3 px-4 text-zinc-200">PNG image</td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-400">Price precision</td>
                <td className="py-3 px-4 text-green-400">Exact values</td>
                <td className="py-3 px-4 text-yellow-400">Visual estimate</td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-400">Pattern recognition</td>
                <td className="py-3 px-4 text-yellow-400">From numbers</td>
                <td className="py-3 px-4 text-green-400">Direct visual</td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-400">API cost</td>
                <td className="py-3 px-4 text-green-400">Lower</td>
                <td className="py-3 px-4 text-yellow-400">Higher</td>
              </tr>
              <tr className="border-b border-dark-border-subtle">
                <td className="py-3 px-4 text-zinc-400">Response time</td>
                <td className="py-3 px-4 text-green-400">Faster</td>
                <td className="py-3 px-4 text-yellow-400">Slower</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-zinc-400">Best for</td>
                <td className="py-3 px-4 text-zinc-200">Quantitative analysis</td>
                <td className="py-3 px-4 text-zinc-200">Technical patterns</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Callout type="tip" title="Which Mode is Better?">
          Neither mode is inherently better. The benchmark aims to discover whether visual
          pattern recognition or numerical analysis leads to better trading decisions. Results
          vary by model and market conditions.
        </Callout>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/models"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Supported Models</div>
          </div>
        </Link>
        <Link
          to="/docs/scoring"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Scoring System</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default TextVsVision;
